import { ApiError } from './api';

/**
 * Sequencing for the customer-initiated session end: the API call that marks
 * the session BEENDET must wait until the upload queue drained, because ended
 * sessions reject presign/complete (410) and queued photos would be lost.
 * Pure logic — queue size and the report call are injected, so the timing
 * rules are unit-testable without a browser.
 */
export class EndReporter {
  private requested = false;
  private reported = false;

  constructor(
    private readonly queuedCount: () => number,
    private readonly report: () => void,
  ) {}

  /** Customer tapped „Beenden" — report now, or after the queue drains. */
  requestEnd(): void {
    this.requested = true;
    this.maybeReport();
  }

  /** Wire to the upload queue's onStatus callback. */
  onQueueStatus(): void {
    this.maybeReport();
  }

  private maybeReport(): void {
    if (!this.requested || this.reported) return;
    if (this.queuedCount() > 0) return; // onQueueStatus fires again after drain
    this.reported = true;
    this.report();
  }
}

/** Upload errors where retrying is pointless: the session was ended (410) or
 * deleted (404) server-side. The queue drops such jobs instead of retrying
 * forever; network errors (status 0) and 5xx keep the retry loop. */
export function isPermanentUploadError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 410 || error.status === 404);
}
